import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ReceiptText } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import {
  getMyFiscalDeclaration,
  professionalFiscalStatus,
} from "@/services/professionalEarningsService";
import { practisesAnyProfession } from "@/lib/is-professional";

/**
 * A standing warning that the professional owes us their fiscal declaration.
 *
 * WHY IT EXISTS. The server has refused bids without a complete declaration
 * since the gate shipped (`quote-bidding.service.ts`, errorCode
 * `fiscal_declaration_required`). Both surfaces can now RESOLVE that refusal
 * when it happens — but only when it happens. A professional learns they are
 * blocked by being blocked, halfway through composing a quote they wanted to
 * send. This tells them before they spend the effort.
 *
 * ── IT IS SHOWN FOR `not_declared` ONLY, AND THAT IS THE POINT (control C-2)
 *
 * `cannot_issue_cfe` is NOT a missing form. It is a professional who opened
 * the form, answered the CFE question honestly with a no, and is now waiting
 * on us — the copy they were given says "escribinos y lo vemos con vos".
 * Nagging them to complete a declaration they already completed is the exact
 * failure the tri-state was introduced to prevent, and it would be worse than
 * silence: it tells someone who did what we asked that they did not.
 *
 * So the warning distinguishes "you owe us a form" from "we owe you an
 * answer", and only speaks to the first. Their status is still visible on the
 * profile card, which is where someone goes to check rather than be told.
 *
 * ── Why it stays quiet while loading, and on failure
 *
 * Rendering the warning before the query resolves would flash "no has
 * declarado" at every professional who is perfectly fine, on every page load.
 * And a failed GET is not evidence of a missing declaration — it is evidence
 * of a failed GET. In both cases the honest render is nothing: the gate itself
 * is still there to catch a real refusal, and it can now be resolved inline.
 */
export function FiscalDeclarationWarning(): JSX.Element | null {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Only professionals bid, so only professionals are ever refused for this.
  // A tenant member who is neither would be asked for a RUT that nothing on
  // the platform will ever read.
  const isProfessional = practisesAnyProfession(user);

  const { data, isLoading, isError } = useQuery({
    // The SAME key the profile card uses, so resolving the declaration there
    // invalidates this warning rather than leaving two caches to disagree.
    queryKey: ["me", "professional", "fiscal-declaration"],
    queryFn: getMyFiscalDeclaration,
    enabled: isProfessional,
  });

  if (!isProfessional || isLoading || isError) return null;
  if (professionalFiscalStatus(data) !== "not_declared") return null;

  return (
    <Alert
      variant="warning"
      className="mt-4 transition-opacity duration-150 motion-reduce:transition-none"
    >
      <ReceiptText aria-hidden />
      <AlertTitle>{t("profile.fiscal.warningTitle")}</AlertTitle>
      <AlertDescription className="text-amber-900/90 dark:text-amber-200/90">
        {t("profile.fiscal.warningDescription")}{" "}
        <Link to="/profile" className="font-medium underline underline-offset-4">
          {t("profile.fiscal.warningAction")}
        </Link>
      </AlertDescription>
    </Alert>
  );
}
